import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { Button, SearchField } from "@legends/ui";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import DataGrid from "./DataGrid";
import {
  configureRecording,
  fetchCaveEvents,
  getPlatformRecordingDefault,
  listCaveRecordings,
  rearmRecording,
  recordingMediaUrl,
  replaceRecordingMedia,
  setCaveRecordDefault,
  setPlatformRecordingDefault,
  setRecordingReplaySale,
  updateRecording,
  uploadRecordingMedia,
  type CaveEvent,
  type Recording,
} from "./api";
import { cardGridStyle, cardGroupStyle, formatDateTime, formatPrice, RecordChoice } from "./sections";
import { useListControls } from "./listControls";
import AdminModal from "./admin/AdminModal";

// Operator "Recordings" tab (ADR 0032): configure record + on-demand for an
// event PRE-LIVE, then manage each recording — edit the per-user view cap +
// availability window (enforced), and finalize the captured media.

const STATUS_LABEL: Record<Recording["status"], string> = {
  pending: "Configured (pre-live)",
  recording: "Recording",
  captured: "Captured (awaiting render)",
  rendering: "Rendering",
  ready: "Available on-demand",
  failed: "Failed",
};

// Label a status, falling back to the raw value for anything unexpected.
function statusLabel(status: string): string {
  return (STATUS_LABEL as Record<string, string>)[status] ?? status;
}

// recordingLabel renders `{title} · {date-time}` so a recurring CaveCast that
// repeats the same title is disambiguated on rows/cards (ADR 0099, mirroring the
// ADR 0079 selector). Falls back to the bare title when the start is unknown.
function recordingLabel(rec: Recording) {
  return rec.eventStartsAt ? `${rec.title} · ${formatDateTime(rec.eventStartsAt)}` : rec.title;
}

const label: CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "var(--fs-2xs)",
  fontWeight: 700,
  letterSpacing: "var(--ls-label)",
  textTransform: "uppercase",
  color: "var(--gold)",
};
const field: CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "var(--fs-body-2)",
  padding: "8px 10px",
  borderRadius: "var(--radius)",
  border: "1px solid var(--hairline)",
  background: "var(--surface-quiet)",
  color: "var(--text)",
};
const panel: CSSProperties = {
  border: "1px solid var(--line-strong, rgba(255,255,255,0.14))",
  borderRadius: "var(--radius-lg)",
  // Raised card fill — a tier above the page/container so the card offsets. (The
  // old --surface-base token is undefined in the admin theme, so it rendered
  // transparent — the recording cards showed the near-black page, LSC-368 QA.)
  background: "var(--ink-700, #24242b)",
  boxShadow: "var(--shadow-1, 0 1px 2px rgba(0,0,0,0.45))",
  padding: 14,
  marginBottom: 12,
};

export function CaveRecordingsTab({ caveId, recordDefault }: { caveId: string; recordDefault?: boolean | null }) {
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [events, setEvents] = useState<CaveEvent[]>([]);
  const [error, setError] = useState("");
  // Cave-level recording opt-out (ADR 0080) + the platform default it inherits.
  const [caveRecord, setCaveRecord] = useState<boolean | null>(recordDefault ?? null);
  const [platformDefault, setPlatformDefault] = useState<boolean | null>(null);

  const reload = useCallback(() => {
    listCaveRecordings(caveId).then(setRecordings).catch(() => setRecordings([]));
  }, [caveId]);

  useEffect(() => {
    reload();
    fetchCaveEvents(caveId).then(setEvents).catch(() => setEvents([]));
    getPlatformRecordingDefault().then(setPlatformDefault).catch(() => setPlatformDefault(null));
  }, [caveId, reload]);

  useEffect(() => setCaveRecord(recordDefault ?? null), [recordDefault]);

  // Events without a recording yet are the candidates to configure.
  const configured = new Set(recordings.map((r) => r.eventId));
  const openEvents = events.filter((e) => !configured.has(e.id));

  const statusOptions = useMemo(
    () => [...new Set(recordings.map((r) => r.status))].sort().map((s) => ({ value: s, label: statusLabel(s) })),
    [recordings],
  );
  const { toolbar, result } = useListControls(recordings, {
    searchText: (r) => [r.title],
    searchPlaceholder: "Search recordings…",
    sorts: [
      { key: "event", label: "Event date", cmp: (a, b) => (a.eventStartsAt ?? "").localeCompare(b.eventStartsAt ?? "") },
      { key: "recent", label: "Recently configured", cmp: (a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? "") },
    ],
    facets: [{ key: "status", label: "Status", options: statusOptions, test: (r, v) => r.status === v }],
  });

  return (
    <div>
      <h2 style={{ fontFamily: "var(--font-display)", color: "var(--text)", margin: "0 0 4px" }}>Recordings</h2>
      <p style={{ color: "var(--text-muted)", fontSize: "var(--fs-body-2)", margin: "0 0 12px" }}>
        CaveCasts record <strong>on by default</strong>. Set the cave default below, override per event on the Events
        tab, then fine-tune each recording's per-viewer watch limit and availability — both enforced on playback.
      </p>

      {/* Cave-level recording opt-out (ADR 0080) — overrides the platform default,
          overridden per event. */}
      <div style={{ margin: "0 0 16px" }}>
        <RecordChoice
          label="Record by default"
          value={caveRecord}
          inheritHint={`inherits platform (${platformDefault === false ? "off" : "on"})`}
          onChange={(v) => {
            setError("");
            setCaveRecord(v);
            setCaveRecordDefault(caveId, v).catch((e) => setError(e?.message ?? "could not update"));
          }}
        />
      </div>

      {error && (
        <p role="alert" style={{ color: "var(--ember)", fontSize: "var(--fs-body-2)" }}>
          {error}
        </p>
      )}

      <ConfigureForm
        caveId={caveId}
        events={openEvents}
        onSaved={() => {
          setError("");
          reload();
        }}
        onError={setError}
      />

      {recordings.length === 0 ? (
        <p style={{ color: "var(--text-dim)", fontSize: "var(--fs-body-2)" }}>No recordings configured yet.</p>
      ) : (
        <>
          {toolbar}
          {result.length === 0 ? (
            <p style={{ color: "var(--text-dim)", fontSize: "var(--fs-body-2)" }}>No recordings match your search.</p>
          ) : (
            <div style={cardGroupStyle}>
              <div style={cardGridStyle}>
                {result.map((rec) => (
                  <RecordingRow key={rec.id} rec={rec} onChanged={reload} onError={setError} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ConfigureForm({
  caveId,
  events,
  onSaved,
  onError,
}: {
  caveId: string;
  events: CaveEvent[];
  onSaved: () => void;
  onError: (m: string) => void;
}) {
  const [eventId, setEventId] = useState("");
  const [title, setTitle] = useState("");
  // Ratified MVP defaults (ADR 0079): 5 views/user, 720h (30-day) availability.
  const [maxViews, setMaxViews] = useState("5");
  const [hours, setHours] = useState("720");

  const submit = () => {
    if (!eventId) return;
    configureRecording(caveId, eventId, {
      title: title.trim() || undefined,
      maxViewsPerUser: Number(maxViews) || 0,
      availabilityHours: Number(hours) || 0,
    })
      .then(() => {
        setTitle("");
        setEventId("");
        onSaved();
      })
      .catch((e) => onError(e?.message ?? "could not configure recording"));
  };

  return (
    <div style={panel}>
      <div style={label}>Record an upcoming event</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "end", marginTop: 8 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: "var(--fs-2xs)", color: "var(--text-muted)" }}>Event</span>
          <select aria-label="Event" style={field} value={eventId} onChange={(e) => setEventId(e.target.value)}>
            <option value="">Select an event…</option>
            {events.map((e) => (
              <option key={e.id} value={e.id}>
                {/* Include date/time + title: recurring CaveCasts repeat the title,
                    so the occurrence needs its start to be selectable (ADR 0079). */}
                {e.title} · {formatDateTime(e.startsAt)}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: "var(--fs-2xs)", color: "var(--text-muted)" }}>Views / user (0 = ∞)</span>
          <input aria-label="Views per user" style={{ ...field, width: 120 }} type="number" min={0} value={maxViews} onChange={(e) => setMaxViews(e.target.value)} />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: "var(--fs-2xs)", color: "var(--text-muted)" }}>Available (hours, 0 = ∞; 720 = 30 days)</span>
          <input aria-label="Availability hours" style={{ ...field, width: 150 }} type="number" min={0} value={hours} onChange={(e) => setHours(e.target.value)} />
        </label>
        <Button onClick={submit} disabled={!eventId}>
          Configure recording
        </Button>
      </div>
      {events.length === 0 && (
        <p style={{ color: "var(--text-dim)", fontSize: "var(--fs-2xs)", margin: "8px 0 0" }}>
          Every event already has a recording, or none are scheduled.
        </p>
      )}
    </div>
  );
}

function RecordingRow({ rec, onChanged, onError }: { rec: Recording; onChanged: () => void; onError: (m: string) => void }) {
  const [maxViews, setMaxViews] = useState(String(rec.maxViewsPerUser));
  const [hours, setHours] = useState(String(rec.availabilityHours));

  const save = () => {
    updateRecording(rec.id, { maxViewsPerUser: Number(maxViews) || 0, availabilityHours: Number(hours) || 0 })
      .then(onChanged)
      .catch((e) => onError(e?.message ?? "could not update recording"));
  };

  const finalize = (file?: File | null) => {
    if (!file) return;
    uploadRecordingMedia(rec.id, file)
      .then(onChanged)
      .catch((e) => onError(e?.message ?? "could not upload media"));
  };

  return (
    <div style={{ ...panel, marginBottom: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
        <strong style={{ color: "var(--text)", fontFamily: "var(--font-display)" }}>
          {recordingLabel(rec)}
          {rec.rehearsal && (
            <span
              aria-label="Rehearsal"
              style={{ marginLeft: 8, fontSize: "var(--fs-2xs)", fontWeight: 600, letterSpacing: "0.04em", color: "var(--ember)", border: "1px solid var(--ember)", borderRadius: 6, padding: "1px 6px", verticalAlign: "middle" }}
            >
              REHEARSAL
            </span>
          )}
        </strong>
        <span style={{ fontSize: "var(--fs-2xs)", color: rec.status === "ready" ? "var(--gold)" : "var(--text-muted)" }}>
          {statusLabel(rec.status)}
        </span>
      </div>
      {rec.status === "failed" && rec.failureReason && (
        <p role="alert" style={{ color: "var(--ember)", fontSize: "var(--fs-2xs)", margin: "6px 0 0" }}>
          Failed: {rec.failureReason}
        </p>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "end", marginTop: 10 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: "var(--fs-2xs)", color: "var(--text-muted)" }}>Views / user (0 = ∞)</span>
          <input aria-label={`Views per user for ${rec.title}`} style={{ ...field, width: 120 }} type="number" min={0} value={maxViews} onChange={(e) => setMaxViews(e.target.value)} />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: "var(--fs-2xs)", color: "var(--text-muted)" }}>Available (hours, 0 = ∞; 720 = 30 days)</span>
          <input aria-label={`Availability hours for ${rec.title}`} style={{ ...field, width: 150 }} type="number" min={0} value={hours} onChange={(e) => setHours(e.target.value)} />
        </label>
        <Button variant="muted" onClick={save}>
          Save limits
        </Button>
        {rec.status !== "ready" && (
          <label style={{ ...field, cursor: "pointer" }}>
            Finalize (upload media)
            <input
              aria-label={`Upload media for ${rec.title}`}
              type="file"
              accept="video/mp4,video/webm,.mp4,.webm,.mov,.m4v"
              style={{ display: "none" }}
              onChange={(e) => finalize(e.target.files?.[0])}
            />
          </label>
        )}
      </div>
      {rec.availableUntil && (
        <p style={{ fontSize: "var(--fs-2xs)", color: "var(--text-dim)", margin: "8px 0 0" }}>
          Available until {new Date(rec.availableUntil).toLocaleString()}.
        </p>
      )}
    </div>
  );
}

// Super-admin "Recordings" surface (ADR 0041): a READ-ONLY view of a cave's
// recordings for support/recovery — status, window, per-user cap, created — with
// the re-arm action as the only write (the recovery control). The full
// configure/edit form stays in the cave console; this is visibility + recovery.
export function AdminCaveRecordingsTab({ caveId }: { caveId: string }) {
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [q, setQ] = useState(""); // global quick-filter over the grid (ADR 0207)
  // Platform-wide recording default (ADR 0080) — global, not cave-specific.
  const [platformDefault, setPlatformDefault] = useState<boolean | null>(null);
  // Replay sale editor (LSC-551 / ADR 0237): the recording being edited + draft state.
  const [replayEdit, setReplayEdit] = useState<Recording | null>(null);

  const reload = useCallback(() => {
    listCaveRecordings(caveId)
      .then(setRecordings)
      .catch(() => setRecordings([]));
  }, [caveId]);

  useEffect(() => reload(), [reload]);
  useEffect(() => {
    getPlatformRecordingDefault().then(setPlatformDefault).catch(() => setPlatformDefault(null));
  }, []);

  const rearm = useCallback(
    (rec: Recording) => {
      setBusy(rec.id);
      setError("");
      rearmRecording(rec.id)
        .then(() => reload())
        .catch((e) => setError(e?.message ?? "could not re-arm recording"))
        .finally(() => setBusy(null));
    },
    [reload],
  );

  // Atomically replace a recording's media asset (LSC-488) — a corrected / re-rendered
  // file. Identity/entitlement/cap/window are preserved; a bad upload leaves the prior
  // good asset serving (the server validates + swaps only on success).
  const replaceMedia = useCallback(
    (rec: Recording, file: File | null) => {
      if (!file) return;
      setBusy(rec.id);
      setError("");
      replaceRecordingMedia(rec.id, file)
        .then(() => reload())
        .catch((e) => setError(e?.message ?? "could not replace media"))
        .finally(() => setBusy(null));
    },
    [reload],
  );

  // Read-only recordings grid (ADR 0156): sortable/filterable columns, with the
  // re-arm recovery action modeled as a custom action column (NOT the generic
  // inline delete). Every column mirrors the retired hand-rolled table.
  const columns = useMemo<ColDef<Recording>[]>(
    () => [
      {
        colId: "recording",
        headerName: "Recording",
        minWidth: 220,
        flex: 2,
        // `{title} · {date-time}` disambiguates a recurring CaveCast (ADR 0099).
        valueGetter: (p) => (p.data ? recordingLabel(p.data) : ""),
      },
      {
        colId: "status",
        headerName: "Status",
        minWidth: 170,
        valueGetter: (p) => (p.data ? statusLabel(p.data.status) : ""),
        cellStyle: (p) => ({ color: p.data?.status === "ready" ? "var(--gold)" : "var(--text)" }),
      },
      {
        colId: "failure",
        headerName: "Failure reason",
        minWidth: 170,
        valueGetter: (p) => p.data?.failureReason ?? "",
        cellStyle: { color: "var(--ember)" },
      },
      {
        colId: "views",
        headerName: "Views / user",
        minWidth: 120,
        valueGetter: (p) => (p.data ? (p.data.maxViewsPerUser > 0 ? String(p.data.maxViewsPerUser) : "∞") : ""),
      },
      {
        colId: "window",
        headerName: "Window",
        minWidth: 110,
        valueGetter: (p) => (p.data ? (p.data.availabilityHours > 0 ? `${p.data.availabilityHours}h` : "∞") : ""),
      },
      {
        colId: "availableUntil",
        headerName: "Available until",
        minWidth: 150,
        valueGetter: (p) => (p.data?.availableUntil ? new Date(p.data.availableUntil).toLocaleString() : "—"),
      },
      {
        colId: "created",
        headerName: "Created",
        minWidth: 120,
        valueGetter: (p) => (p.data?.createdAt ? new Date(p.data.createdAt).toLocaleDateString() : "—"),
      },
      {
        colId: "media",
        headerName: "Media",
        minWidth: 190,
        maxWidth: 220,
        sortable: false,
        filter: false,
        // Download / atomically replace the replay asset (LSC-488) — only when there IS
        // an asset (status ready). Replace preserves identity/cap/window; a bad upload
        // leaves the current asset intact.
        cellRenderer: (p: ICellRendererParams<Recording>) => {
          if (!p.data || p.data.status !== "ready") return <span style={{ color: "var(--text-muted)" }}>—</span>;
          const rec = p.data;
          return (
            <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
              <a href={recordingMediaUrl(rec.id)} target="_blank" rel="noreferrer" style={{ color: "var(--gold)", fontSize: "var(--fs-body-2)" }}>
                Download
              </a>
              <label style={{ color: "var(--text-link)", cursor: busy === rec.id ? "default" : "pointer", fontSize: "var(--fs-body-2)" }}>
                {busy === rec.id ? "Replacing…" : "Replace"}
                <input
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime,.mp4,.m4v,.mov,.webm"
                  aria-label={`Replace media for ${recordingLabel(rec)}`}
                  disabled={busy === rec.id}
                  style={{ display: "none" }}
                  onChange={(e) => replaceMedia(rec, e.target.files?.[0] ?? null)}
                />
              </label>
            </span>
          );
        },
      },
      {
        colId: "replaySale",
        headerName: "Replay sale",
        minWidth: 150,
        maxWidth: 180,
        sortable: false,
        filter: false,
        // Enable/disable paid replay access + set the price (LSC-551 / ADR 0237). Only
        // a ready recording can be sold (there's an asset to watch). Opens a small
        // modal; the server gates on catalog:write (admin price authority).
        cellRenderer: (p: ICellRendererParams<Recording>) => {
          if (!p.data || p.data.status !== "ready") return <span style={{ color: "var(--text-muted)" }}>—</span>;
          const rec = p.data;
          return (
            <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
              <span style={{ color: rec.replaySaleEnabled ? "var(--gold)" : "var(--text-muted)", fontSize: "var(--fs-body-2)" }}>
                {rec.replaySaleEnabled ? formatPrice(rec.replayPriceCents ?? 0) : "Off"}
              </span>
              <button
                type="button"
                onClick={() => setReplayEdit(rec)}
                style={{ background: "none", border: "none", color: "var(--text-link)", cursor: "pointer", fontSize: "var(--fs-body-2)" }}
              >
                Edit
              </button>
            </span>
          );
        },
      },
      {
        colId: "recover",
        headerName: "Recover",
        minWidth: 130,
        maxWidth: 150,
        sortable: false,
        filter: false,
        cellRenderer: (p: ICellRendererParams<Recording>) =>
          p.data ? (
            <Button variant="muted" onClick={() => rearm(p.data as Recording)} disabled={busy === p.data.id}>
              {busy === p.data.id ? "Re-arming…" : "Re-arm"}
            </Button>
          ) : null,
      },
    ],
    [rearm, replaceMedia, busy],
  );

  return (
    <div>
      <h2 style={{ fontFamily: "var(--font-display)", color: "var(--text)", margin: "0 0 4px" }}>Recordings</h2>
      <p style={{ color: "var(--text-muted)", fontSize: "var(--fs-body-2)", margin: "0 0 12px" }}>
        Support &amp; recovery view of this cave&rsquo;s recordings. Configuration lives in the cave console;
        <strong> re-arm</strong> resets a used recording so its event can go live and record fresh, and
        <strong> Download / Replace</strong> let you pull or atomically swap a ready recording&rsquo;s media asset
        (a corrected / re-rendered file — identity, entitlement, view cap, and window are preserved).
      </p>

      {/* Platform-wide recording default (ADR 0080) — the base of the opt-out
          hierarchy (cave and event overrides win over it). */}
      <div style={{ margin: "0 0 16px", display: "flex", alignItems: "center", gap: 10 }}>
        <RecordChoice
          label="Platform-wide: record CaveCasts by default"
          allowInherit={false}
          value={platformDefault ?? true}
          onChange={(v) => {
            setError("");
            setPlatformDefault(v);
            setPlatformRecordingDefault(v === true).catch((e) => setError(e?.message ?? "could not update"));
          }}
        />
      </div>

      {error && (
        <p role="alert" style={{ color: "var(--ember)", fontSize: "var(--fs-body-2)" }}>
          {error}
        </p>
      )}

      <div style={{ margin: "0 0 10px" }}>
        <SearchField value={q} onChange={setQ} placeholder="Search recordings…" aria-label="Search recordings" width={280} />
      </div>
      <DataGrid<Recording>
        rows={recordings}
        columns={columns}
        getRowId={(r) => r.id}
        label="Recordings"
        quickFilterText={q}
        emptyText="No recordings for this cave."
      />

      {replayEdit && (
        <ReplaySaleModal
          rec={replayEdit}
          onClose={() => setReplayEdit(null)}
          onSaved={() => {
            setReplayEdit(null);
            reload();
          }}
          onError={(m) => setError(m)}
        />
      )}
    </div>
  );
}

// ReplaySaleModal: enable/disable paid replay access on a recording + set the price
// (LSC-551 / ADR 0237). The price is flat + independent of the live-event price — a
// working ~$5 default. The server provisions a hidden replay ticket type the Fan's
// "Buy Ticket" checkout then purchases; the view cap + window are unchanged.
function ReplaySaleModal({
  rec,
  onClose,
  onSaved,
  onError,
}: {
  rec: Recording;
  onClose: () => void;
  onSaved: () => void;
  onError: (m: string) => void;
}) {
  const [enabled, setEnabled] = useState(rec.replaySaleEnabled ?? false);
  const [price, setPrice] = useState(() => ((rec.replayPriceCents ?? 500) / 100).toFixed(2));
  const [busy, setBusy] = useState(false);

  const save = () => {
    const dollars = Number(price);
    if (enabled && (!Number.isFinite(dollars) || dollars < 0)) {
      onError("Enter a valid price.");
      return;
    }
    setBusy(true);
    onError("");
    setRecordingReplaySale(rec.id, enabled, Math.round((Number.isFinite(dollars) ? dollars : 0) * 100))
      .then(() => onSaved())
      .catch((e) => onError(e?.message ?? "could not update replay sale"))
      .finally(() => setBusy(false));
  };

  const field: CSSProperties = {
    width: "100%",
    padding: "8px 10px",
    borderRadius: "var(--radius)",
    border: "1px solid var(--border)",
    background: "var(--surface-1)",
    color: "var(--text-1)",
  };

  return (
    <AdminModal title="Replay sale" confirmLabel="Save" busy={busy} onConfirm={save} onCancel={onClose}>
      <p style={{ color: "var(--text-muted)", fontSize: "var(--fs-body-2)", margin: "0 0 12px" }}>
        Sell replay access to fans who don&rsquo;t already hold a ticket for this CaveCast. The price is independent of
        the live-event price; existing ticket/pass holders and VIPs watch for free.
      </p>
      <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, color: "var(--text-1)" }}>
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} aria-label="Sell replay access" />
        Sell replay access
      </label>
      <label style={{ display: "block", color: "var(--text-muted)", fontSize: "var(--fs-body-2)", marginBottom: 4 }}>
        Replay price (USD)
      </label>
      <input
        type="number"
        min="0"
        step="0.01"
        value={price}
        disabled={!enabled}
        onChange={(e) => setPrice(e.target.value)}
        aria-label="Replay price in dollars"
        style={{ ...field, opacity: enabled ? 1 : 0.5 }}
      />
    </AdminModal>
  );
}
